import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import getFullNameWithTitle from '@medflex/utils/getFullNameWithTitle';
import InformationCircleIcon from '@heroicons/react/24/outline/InformationCircleIcon';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import DropDown from '../DropDown/DropDown';
import SessionsAddParticipantForm from './SessionsAddParticipantForm';
import SessionsParticipantList from './SessionsParticipantList';
import SessionsParticipantListItem from './SessionsParticipantListItem';

const Participants = ({
  maxParticipants,
  onParticipantsChanged,
  defaultParticipants,
  moderators,
  contacts,
  onModeratorChaned,
  canSendSms,
  smsUpgradeUrl,
}) => {
  const [participants, setParticipants] = useState(defaultParticipants || []);
  // in case of a 1-1 meeting with an external participant, the addParticipantForm needs to be active
  // because in this case, the participant will not appear in the participant list
  const [isAddParticipantFormActive, setIsAddParticipantFormActive] = useState(
    !defaultParticipants ||
      (maxParticipants === 2 && defaultParticipants.length && !!defaultParticipants[0].reference),
  );
  const { t } = useTranslation('messenger');
  const handleModeratorChange = event => {
    const filteredParticipants = participants.filter(p => p.id !== event.target.value);
    onModeratorChaned(event.target.value);
    setParticipants(filteredParticipants);
  };

  const handleParticipantAdded = data => {
    if (maxParticipants > 2) {
      if (
        !participants.find(
          participant =>
            (participant.reference && participant.reference === data.reference) ||
            (participant.userId && participant.userId === data.userId),
        )
      ) {
        setParticipants([...participants, data]);
        setIsAddParticipantFormActive(false);
      }
    } else if (
      !participants.length ||
      participants.some(p => Object.keys(p).some(key => p[key] !== data[key]))
    )
      setParticipants([data]);
  };

  const handleDeleteParticipant = data => {
    if (data) {
      const { userId, reference } = data;
      if (typeof userId !== 'undefined') {
        setParticipants(participants.filter(participant => participant.userId !== userId));
      }

      if (typeof reference !== 'undefined') {
        setParticipants(participants.filter(participant => participant.reference !== reference));
      }
    } else if (participants.length) setParticipants([]);
  };

  const handleShowParticipantFormClicked = e => {
    e.preventDefault();
    setIsAddParticipantFormActive(true);
  };

  useEffect(() => {
    onParticipantsChanged(participants);
    if (participants.length === 0) setIsAddParticipantFormActive(true);
  }, [participants]);

  useEffect(() => {
    if (defaultParticipants) {
      setParticipants(defaultParticipants);
    }
  }, [defaultParticipants]);

  return (
    <>
      {moderators && moderators.options && moderators.options.length > 1 && (
        <section className="mb-6">
          <h4 className="text-xl mb-2 font-semibold">
            {t('SessionsCreateModule.Participants.chooseModerator')}
          </h4>
          <div className="bg-slate-100 p-2 lg:p-4 rounded">
            <DropDown
              options={moderators.options}
              optionLabels={moderators.labels}
              onChange={handleModeratorChange}
            />
          </div>
        </section>
      )}
      <section className="mb-6">
        <div className="flex items-center mb-2">
          <h3 className="text-xl font-semibold">
            {t(
              `SessionsCreateModule.Participants.chooseParticipant.title.${
                maxParticipants === 2 ? 'single' : 'multiple'
              }`,
              { allowHtml: true },
            )}
          </h3>
          {maxParticipants > 2 && (
            <a
              href="https://medflex.de/hilfe/behandler-video/videotermine-durchfuehren/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 ml-2 flex items-center"
            >
              <InformationCircleIcon className="h-8 w-8" />
            </a>
          )}
        </div>
        {!(maxParticipants === 2 && !(participants[0] && participants[0].userId)) && (
          <SessionsParticipantList className="mb-2">
            {participants.map(participant => (
              <SessionsParticipantListItem
                key={participant.userId || participant.reference}
                reference={participant.reference}
                onDelete={() => handleDeleteParticipant(participant)}
              >
                {participant.fullNameWithTitle ||
                  getFullNameWithTitle({ id: participant.userId, ...participant })}
              </SessionsParticipantListItem>
            ))}
          </SessionsParticipantList>
        )}
        {(maxParticipants === 2
          ? !(participants[0] && participants[0].userId)
          : participants.length < maxParticipants) && (
          <>
            {isAddParticipantFormActive && (
              <SessionsAddParticipantForm
                contacts={contacts}
                onSubmit={handleParticipantAdded}
                maxParticipants={maxParticipants}
                onDeleteParticipant={handleDeleteParticipant}
                defaultParticipant={
                  (defaultParticipants &&
                    defaultParticipants.length &&
                    maxParticipants === 2 &&
                    defaultParticipants[0]) ||
                  null
                }
                canSendSms={canSendSms}
                smsUpgradeUrl={smsUpgradeUrl}
              />
            )}
            {!isAddParticipantFormActive && (
              <button
                type="button"
                className="flex items-center text-blue-500 hover:underline cursor-pointer"
                onClick={handleShowParticipantFormClicked}
              >
                <PlusCircleIcon className="mt-1 w-7 h-7" />
                <span className="ml-2">
                  {t('SessionsCreateModule.Participants.chooseParticipant.add')}
                </span>
              </button>
            )}
          </>
        )}
      </section>
    </>
  );
};

Participants.propTypes = {
  maxParticipants: PropTypes.number,
  onParticipantsChanged: PropTypes.func.isRequired,
  onModeratorChaned: PropTypes.func.isRequired,
  defaultParticipants: PropTypes.arrayOf(PropTypes.any),
  moderators: PropTypes.shape({
    options: PropTypes.any,
    labels: PropTypes.any,
  }),
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.string,
      fullNameWithTitle: PropTypes.string,
      avatar: PropTypes.string,
      initials: PropTypes.string,
    }),
  ),
  canSendSms: PropTypes.bool.isRequired,
  smsUpgradeUrl: PropTypes.string,
};

Participants.defaultProps = {
  maxParticipants: 9,
  smsUpgradeUrl: '',
};

export default Participants;
